import React, { StrictMode, Suspense, useEffect } from 'react';
import { createGlobalStyle } from 'styled-components';
import ErrorBoundary from 'common/ErrorBoundary';
import { ThemeProviders } from './withRoot';
import { useMousetrap } from './utils/mousetraps';
import Mousetrap from 'mousetrap';
import { Colors } from 'styles';
import { FN_KEYS } from 'config/constants';
import { RecoilRoot } from 'recoil';
import { useAnalytics, useCaseAnalytics } from 'hooks/useAnalytics';
import { useCrossTabNotifier } from 'hooks/useCrossTabNotifier';
import { ToasterProvider } from 'common/ui/Toaster';
import { CompactUIProvider, useCompactUI } from 'hooks/useCompactUI';
import { AppRoutes } from 'common/Layouts/Routing/Routing';
import { BrowserRouter, useLocation } from 'react-router-dom';
import analytics from 'modules/analytics';
import { useIsAuthenticated } from './hooks/useAuth';
import { useViewerSettingsSync } from './hooks/useViewerSettingsSync';
import { useGetCurrentUserFeatureFlags } from 'modules/feature-flags';
import { DatadogSharedContextApplier } from './DatadogSharedContextApplier';
import { useExtensionState } from './hooks/useExtensionState';
import { useRouteTransitions } from './hooks/useRouteTransitions';
import { useClearCase } from './hooks/useClearCase';
import { InactivityTracking } from 'common/InactivityTracking/InactivityTracking';
import { SessionExpirationNotice } from 'common/SessionExpirationNotice';
import { getScreenName } from 'utils/screenNames';
import { ScreenName } from 'generated/graphql';
import { useCheckUserRISPermissions } from 'integrations/RIS/checkPermissions';
import { RoleCheckFailure } from 'integrations/RIS/RoleCheckFailure';

import { PageTypes, getPageType } from 'utils/pageTypes';
import { useKeepAliveTab } from 'hooks/useKeepAliveTab';
import { CurrentCaseProvider, useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { useExternalLaunchLifecycle } from 'common/External/useExternalLaunchLifecycle';
import { useCaptureWorkstationIDFromQuery } from 'common/External/useCaptureWorkstationIDFromQuery';
import { telemetryContextFor } from 'modules/Apollo/types';
import { usePatchUserSettings } from 'hooks/usePatchUserSettings';
import { useWindowRectsSanitizer } from 'hooks/useWindowsRects';
import { useWorkspaceDisplayArrangement } from 'common/WorkspaceLayoutManager/useWorkspaceDisplayArrangement';
import { useAutoApplyWorkspaceLayoutPreset } from 'common/WorkspaceLayoutManager/useAutoApplyWorkspaceLayoutPreset';
import { OpenTabsSyncProvider } from 'hooks/useOpenTabs';
import { AnnouncementBanner } from 'common/AnnouncementBanner';
import { AppQueryProviders } from './AppQueryProviders';
import { isLogoutPathname } from 'utils/router';
import { useClusterRedirects } from 'hooks/useConfigureClusterConnections';

function SessionReplayRecorder() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.includes('/reporter') || location.pathname.includes('/worklist')) {
      analytics.startSessionReplayRecording();
    }
    if (location.pathname.includes('/viewer')) {
      analytics.stopSessionReplayRecording();
    }
  }, [location]);

  return null;
}

const GlobalStyle = createGlobalStyle`
  body {
    background: ${Colors.gray1};
  }
`;

const App = () => {
  useAnalytics({ context: APOLLO_QUERY_CONTEXT });
  useMousetrap();
  useCrossTabNotifier();
  useViewerSettingsSync({ context: APOLLO_QUERY_CONTEXT });
  useGetCurrentUserFeatureFlags();
  useExtensionState();
  useRouteTransitions();
  useClearCase();
  useKeepAliveTab();
  usePatchUserSettings();
  useWindowRectsSanitizer();
  useWorkspaceDisplayArrangement();
  useAutoApplyWorkspaceLayoutPreset({ context: APOLLO_QUERY_CONTEXT });
  useClusterRedirects();

  // clears all cached data on browser close
  // disabled for now
  // useClearStoredDataOnClose();
  const { resizeListener } = useCompactUI();

  useEffect(() => {
    Mousetrap.addKeycodes(FN_KEYS);
  }, []);

  return (
    <div css="width: 100vw; height: 100%; display: flex;">
      {resizeListener}
      <GlobalStyle />
      <Suspense>
        <AppRoutes />
      </Suspense>
      <SessionReplayRecorder />
      <DatadogSharedContextApplier />
      <Suspense>
        <AuthorizedWrapper>
          <AuthorizedAnalytics />
        </AuthorizedWrapper>
      </Suspense>
    </div>
  );
};

const AuthorizedAnalytics = () => {
  useCaseAnalytics({ context: APOLLO_QUERY_CONTEXT });
  return null;
};

type AuthorizedWrapperProps = {
  children: React.ReactNode;
};

const AuthorizedWrapper = ({ children }: AuthorizedWrapperProps) => {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  return isAuthenticated && !isLogoutPathname(location.pathname) ? children : null;
};

const RoleCheckWrapper = ({ children }: { children: React.ReactElement }) => {
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();

  const { canAccessWorklist, canAccessReporter, canAccessViewer, canAccessSecondViewer } =
    useCheckUserRISPermissions();

  if (!isAuthenticated) return children;

  const pageType = getPageType(location.pathname);

  const routeRoleCheck = () => {
    switch (pageType) {
      case PageTypes.REPORTER:
        return canAccessReporter;
      case PageTypes.WORKLIST:
      case PageTypes.PATIENT_JACKET:
        return canAccessWorklist;
      case PageTypes.VIEWER:
        const screen = getScreenName(location.pathname);
        return screen === ScreenName.Viewer0 ? canAccessViewer : canAccessSecondViewer;
      default:
        return true;
    }
  };

  return routeRoleCheck() ? children : <RoleCheckFailure />;
};

const ExternalLaunchLifecycleManager = () => {
  const { currentCaseId } = useCurrentCaseContext();
  useExternalLaunchLifecycle(currentCaseId);
  return null;
};

const WorkstationIDQueryCaptureManager = () => {
  useCaptureWorkstationIDFromQuery();
  return null;
};

const DecoratedApp = (): React.ReactElement => (
  // with BrowserRouter we can depend on useLocation updating AFTER window.location
  // this is very important for useOpenTabs and all of the data that depends on it
  // namely current case and current worklist id
  // previous issue: https://sironamedical.atlassian.net/browse/EN-5488
  // make sure to test choosing new cases from the worklist when updating router code
  <BrowserRouter>
    <ThemeProviders>
      <RecoilRoot>
        <AppQueryProviders context={APOLLO_QUERY_CONTEXT}>
          {/* Keep query owners above StrictMode so its development remount does not leave
              duplicate Apollo observables behind. */}
          <StrictMode>
            <OpenTabsSyncProvider>
              <ToasterProvider>
                <div css="height: 100%; display: flex; flex-direction: column;">
                  <AuthorizedWrapper>
                    <Suspense>
                      <InactivityTracking />
                      <SessionExpirationNotice />
                    </Suspense>
                    <AnnouncementBanner />
                  </AuthorizedWrapper>
                  {/* The banner occupies its natural height above this wrapper. This flex item takes
                          the remaining viewport space, and min-height: 0 keeps workspace scrolling inside
                          the app instead of allowing its content to make #root taller than the viewport. */}
                  <div css="display: flex; flex: 1; min-height: 0;">
                    <ErrorBoundary name="root">
                      <CurrentCaseProvider>
                        <WorkstationIDQueryCaptureManager />
                        <ExternalLaunchLifecycleManager />
                        <CompactUIProvider>
                          <RoleCheckWrapper>
                            <App />
                          </RoleCheckWrapper>
                        </CompactUIProvider>
                      </CurrentCaseProvider>
                    </ErrorBoundary>
                  </div>
                </div>
              </ToasterProvider>
            </OpenTabsSyncProvider>
          </StrictMode>
        </AppQueryProviders>
      </RecoilRoot>
    </ThemeProviders>
  </BrowserRouter>
);

export default DecoratedApp;

DecoratedApp.displayName = 'App';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(DecoratedApp);
